import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  getQuoteApiBase,
  quoteAdminHeaders,
  rewriteCustomerLink,
} from "../../utils/quoteApiBase";
import {
  printProformaHtml,
  downloadProformaPdf,
} from "../../utils/proformaDownload";
import IrrPriceInput from "../../components/IrrPriceInput";
import {
  parseIrrInput,
  parseDiscountPercentInput,
  formatDiscountPercent,
} from "../../utils/irrPriceFormat";
import { playOrderNotificationChime } from "../../utils/orderNotificationChime";
import styles from "./GetQuotes.module.css";

const CUSTOMER_PROFORMA_STATUSES = [
  "QUOTE_APPROVED",
  "QUOTE_SENT",
  "AWAITING_PAYMENT",
  "PAID_VERIFIED",
  "COMPLETED",
];

function toman(irr) {
  if (irr == null) return "—";
  return Math.floor(Number(irr) / 10).toLocaleString("fa-IR");
}

function quoteIdOf(q) {
  if (!q) return "";
  if (typeof q === "string") return q;
  if (q._id != null) return String(q._id);
  if (q.id != null) return String(q.id);
  return "";
}

async function safeJsonFetch(url, opts) {
  const res = await fetch(url, opts);
  let json = {};
  try {
    json = await res.json();
  } catch (_) {
    json = {};
  }
  return { res, json };
}

const STATUS_FA = {
  QUOTE_REQUESTED: "درخواست ثبت‌شده",
  PRICING_IN_PROGRESS: "در حال قیمت‌گذاری",
  QUOTE_READY: "آماده تأیید",
  QUOTE_APPROVED: "تأییدشده",
  QUOTE_SENT: "ارسال‌شده",
  AWAITING_PAYMENT: "در انتظار پرداخت",
  PAID_VERIFIED: "پرداخت تأییدشده",
  PREPARING: "آماده‌سازی",
  SHIPPED: "ارسال محموله",
  COMPLETED: "تکمیل‌شده",
  EXPIRED: "منقضی‌شده",
  CANCELED: "لغوشده",
};

const PAYMENT_STATUS_FA = {
  none: "بدون پرداخت",
  pending: "در انتظار پرداخت",
  verified: "پرداخت تأییدشده",
  failed: "پرداخت ناموفق",
};

function formatFaDateTime(value) {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString("fa-IR");
}

function lineUnitFromDraft(line, priceDrafts) {
  const id = String(line._id);
  const draftRaw = priceDrafts[id];
  const draftNum =
    draftRaw === "" || draftRaw == null
      ? null
      : parseIrrInput(String(draftRaw));
  const unit =
    draftNum != null && !Number.isNaN(draftNum)
      ? draftNum
      : line.unit_price_irr != null
        ? Number(line.unit_price_irr)
        : null;
  if (unit == null || !Number.isFinite(unit)) return null;
  return unit;
}

/** Gross line total from base unit × qty (discount never applied here). */
function lineGrossIrr(line, priceDrafts) {
  const unit = lineUnitFromDraft(line, priceDrafts);
  if (unit == null) return null;
  return unit * Number(line.qty || 0);
}

function linePctFromDraft(line, discountDrafts) {
  const id = String(line._id);
  if (Object.prototype.hasOwnProperty.call(discountDrafts, id)) {
    const raw = discountDrafts[id];
    if (raw === "" || raw == null) return 0;
    const n = parseDiscountPercentInput(String(raw));
    return Number.isNaN(n) ? null : n == null ? 0 : n;
  }
  if (line.discount_percent == null || line.discount_percent === "") return 0;
  return Number(line.discount_percent) || 0;
}

function lineDiscountAmountIrr(line, priceDrafts, discountDrafts) {
  const gross = lineGrossIrr(line, priceDrafts);
  const pct = linePctFromDraft(line, discountDrafts);
  if (gross == null || pct == null || Number.isNaN(pct)) return null;
  const scaled = Math.round(pct * 100);
  return Math.round((gross * scaled) / 10000);
}

function lineNetIrr(line, priceDrafts, discountDrafts) {
  const gross = lineGrossIrr(line, priceDrafts);
  const disc = lineDiscountAmountIrr(line, priceDrafts, discountDrafts);
  if (gross == null || disc == null) return null;
  return Math.max(0, gross - disc);
}

/** @deprecated name kept for minimal churn — returns gross */
function lineTotalIrr(line, drafts) {
  return lineGrossIrr(line, drafts);
}

const PENDING_STATUSES = new Set([
  "QUOTE_REQUESTED",
  "PRICING_IN_PROGRESS",
  "QUOTE_READY",
  "QUOTE_APPROVED",
]);
const SENT_STATUSES = new Set([
  "QUOTE_SENT",
  "AWAITING_PAYMENT",
  "PAID_VERIFIED",
  "PREPARING",
  "SHIPPED",
  "COMPLETED",
]);

function statusBadgeClass(status) {
  switch (status) {
    case "QUOTE_REQUESTED":
      return styles.badgeRequested;
    case "PRICING_IN_PROGRESS":
    case "QUOTE_READY":
      return styles.badgePricing;
    case "QUOTE_APPROVED":
      return styles.badgeApproved;
    case "QUOTE_SENT":
      return styles.badgeSent;
    case "AWAITING_PAYMENT":
    case "PAID_VERIFIED":
      return styles.badgePay;
    case "PREPARING":
    case "SHIPPED":
    case "COMPLETED":
      return styles.badgeDone;
    case "EXPIRED":
      return styles.badgeExpired;
    case "CANCELED":
      return styles.badgeCanceled;
    default:
      return styles.badgeDefault;
  }
}

function dayStart(isoDate) {
  if (!isoDate) return null;
  const d = new Date(`${isoDate}T00:00:00`);
  return Number.isNaN(d.getTime()) ? null : d;
}

function dayEnd(isoDate) {
  if (!isoDate) return null;
  const d = new Date(`${isoDate}T23:59:59.999`);
  return Number.isNaN(d.getTime()) ? null : d;
}

/**
 * Isolated quote management under Orders /quote-admin-isolated.
 * Modern scoped UI — preserves all pricing / approve / mock-send workflows.
 */
const GetQuotes = () => {
  const [quotes, setQuotes] = useState([]);
  const [selected, setSelected] = useState(null);
  const [lines, setLines] = useState([]);
  const [unread, setUnread] = useState(0);
  const [soundOn, setSoundOn] = useState(false);
  const [msg, setMsg] = useState("");
  const [msgKind, setMsgKind] = useState("");
  const [connError, setConnError] = useState("");
  const [apiBase, setApiBase] = useState(() => getQuoteApiBase());
  const [priceDraft, setPriceDraft] = useState({});
  const [discountDraft, setDiscountDraft] = useState({});
  const [shippingDraft, setShippingDraft] = useState("");
  const [customerLink, setCustomerLink] = useState("");
  const [loading, setLoading] = useState(true);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState("");
  const [actionBusy, setActionBusy] = useState(false);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const seenIdsRef = useRef(new Set());
  const audioCtxRef = useRef(null);
  const soundOnRef = useRef(false);
  const detailRef = useRef(null);

  function flash(text, kind) {
    setMsg(text || "");
    setMsgKind(kind || (text ? "info" : ""));
  }

  function missingPriceLabels(lineList, drafts) {
    const missing = [];
    (lineList || []).forEach((l) => {
      const id = String(l._id);
      const saved = l.unit_price_irr;
      const draftRaw = drafts[id];
      const draftNum =
        draftRaw === "" || draftRaw == null
          ? null
          : parseIrrInput(String(draftRaw));
      const hasSaved =
        saved != null && Number.isFinite(Number(saved)) && Number(saved) >= 0;
      const hasDraft =
        draftNum != null && !Number.isNaN(draftNum) && draftNum >= 0;
      if (!hasSaved && !hasDraft) {
        const title = String(l.title || "بدون‌عنوان").trim();
        const size = String(l.size || "").trim();
        missing.push(size ? `${title} (سایز ${size})` : title);
      }
    });
    return missing;
  }

  useEffect(() => {
    setApiBase(getQuoteApiBase());
  }, []);

  const playBeepTone = useCallback(() => {
    playOrderNotificationChime(audioCtxRef);
  }, []);

  const beep = useCallback(() => {
    if (!soundOnRef.current) return;
    playBeepTone();
  }, [playBeepTone]);

  const enableSound = useCallback(() => {
    soundOnRef.current = true;
    setSoundOn(true);
    // Unlock / resume AudioContext inside the user gesture (do not rely on stale soundOn).
    playBeepTone();
  }, [playBeepTone]);

  const testSound = useCallback(() => {
    soundOnRef.current = true;
    setSoundOn(true);
    playBeepTone();
  }, [playBeepTone]);

  const refreshList = useCallback(async () => {
    const base = getQuoteApiBase();
    setApiBase(base);
    try {
      const { res, json } = await safeJsonFetch(`${base}/admin/quote_requests`, {
        headers: await quoteAdminHeaders(),
      });
      if (!res.ok) {
        setConnError(`API پاسخ ${res.status} داد (${base})`);
        setQuotes([]);
        return;
      }
      setConnError("");
      setQuotes(json.data || []);
    } catch (err) {
      setQuotes([]);
      setConnError(
        `Failed to fetch — API در دسترس نیست: ${base}. صفحه را روی http://127.0.0.1:18012 و API را روی :18030 باز کنید.`
      );
    } finally {
      setLoading(false);
    }
  }, []);

  const refreshUnread = useCallback(async () => {
    const base = getQuoteApiBase();
    try {
      const { res, json } = await safeJsonFetch(
        `${base}/admin/quote_notifications?unread=1`,
        { headers: await quoteAdminHeaders() }
      );
      if (res.ok) setUnread(json.data?.unread || 0);
    } catch (_) {
      /* ignore */
    }
  }, []);

  const openQuote = useCallback(async (rawId, evt) => {
    if (evt) {
      evt.preventDefault();
      evt.stopPropagation();
    }
    const id = quoteIdOf(rawId);
    flash("", "");
    setCustomerLink("");
    setDetailError("");
    setDetailLoading(true);
    setSelected(null);
    setLines([]);

    if (!id) {
      setDetailLoading(false);
      setDetailError("شناسه درخواست نامعتبر است.");
      return;
    }

    const base = getQuoteApiBase();
    flash(`در حال بارگذاری جزئیات (${id.slice(-6)}…) از ${base}`, "info");

    requestAnimationFrame(() => {
      if (detailRef.current) {
        detailRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    });

    try {
      const { res, json } = await safeJsonFetch(
        `${base}/admin/quote_requests/${encodeURIComponent(id)}`,
        { headers: await quoteAdminHeaders() }
      );
      if (!res.ok || !json.data || !json.data.quote) {
        const errText =
          json.error ||
          json.message ||
          `بارگذاری ناموفق (HTTP ${res.status}) — ${base}/admin/quote_requests/${id}`;
        setDetailError(errText);
        flash("", "");
        setDetailLoading(false);
        return;
      }
      setSelected(json.data.quote);
      setLines(Array.isArray(json.data.lines) ? json.data.lines : []);
      const draft = {};
      const discDraft = {};
      (json.data.lines || []).forEach((l) => {
        draft[String(l._id)] =
          l.unit_price_irr != null ? String(l.unit_price_irr) : "";
        discDraft[String(l._id)] = formatDiscountPercent(
          l.discount_percent == null ? "" : l.discount_percent
        );
      });
      setPriceDraft(draft);
      setDiscountDraft(discDraft);
      setShippingDraft(
        json.data.quote.shipping_irr != null
          ? String(json.data.quote.shipping_irr)
          : "0"
      );
      flash(
        `جزئیات ${json.data.quote.quote_ref} بارگذاری شد — وضعیت: ${
          STATUS_FA[json.data.quote.status] || json.data.quote.status
        }`,
        "info"
      );
      setDetailLoading(false);
      requestAnimationFrame(() => {
        if (detailRef.current) {
          detailRef.current.scrollIntoView({
            behavior: "smooth",
            block: "start",
          });
        }
      });
    } catch (err) {
      setDetailError(
        `خطای شبکه هنگام بارگذاری جزئیات از ${base}. تونل ۱۸۰۳۰ را بررسی کنید.`
      );
      flash("", "");
      setDetailLoading(false);
    }
  }, []);

  const closeDetail = useCallback(() => {
    setSelected(null);
    setLines([]);
    setDetailError("");
    setDetailLoading(false);
    setCustomerLink("");
    flash("", "");
    setActionBusy(false);
  }, []);

  const markNotificationsRead = useCallback(
    async (ids) => {
      if (!ids.length) return;
      const base = getQuoteApiBase();
      try {
        await safeJsonFetch(`${base}/admin/quote_notifications/mark_read`, {
          method: "POST",
          headers: await quoteAdminHeaders(),
          body: JSON.stringify({ ids }),
        });
        refreshUnread();
      } catch (_) {
        flash("علامت‌گذاری خوانده‌شده ناموفق", "error");
      }
    },
    [refreshUnread]
  );

  useEffect(() => {
    refreshList();
    refreshUnread();
  }, [refreshList, refreshUnread]);

  // Authenticated poll (no EventSource ?admin=1 bypass — production-safe)
  useEffect(() => {
    let cancelled = false;
    let since = new Date().toISOString();
    const tick = async () => {
      const base = getQuoteApiBase();
      try {
        const { res, json } = await safeJsonFetch(
          `${base}/admin/quote_notifications/poll?since=${encodeURIComponent(since)}`,
          { headers: await quoteAdminHeaders() }
        );
        if (cancelled || !res.ok) return;
        const docs = json.data?.docs || [];
        if (typeof json.data?.unread === "number") setUnread(json.data.unread);
        if (json.data?.server_time) since = json.data.server_time;
        const fresh = docs.filter((d) => {
          if (seenIdsRef.current.has(d._id)) return false;
          seenIdsRef.current.add(d._id);
          return true;
        });
        if (fresh.length) {
          beep();
          refreshList();
        }
      } catch (_) {
        /* ignore */
      }
    };
    tick();
    const id = setInterval(tick, 12000);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [beep, refreshList]);

  async function savePrice(lineId, opts) {
    if (!selected) return false;
    const quiet = opts && opts.quiet;
    const base = getQuoteApiBase();
    const id = String(lineId);
    const raw = priceDraft[id];
    const unit = parseIrrInput(raw == null ? "" : String(raw));
    if (unit == null || Number.isNaN(unit)) {
      if (!quiet) {
        flash(
          "قیمت نامعتبر است. یک عدد صحیح غیرمنفی به ریال وارد کنید (بدون اعشار).",
          "error"
        );
      }
      return false;
    }
    const discRaw = Object.prototype.hasOwnProperty.call(discountDraft, id)
      ? discountDraft[id]
      : "";
    const discParsed = parseDiscountPercentInput(
      discRaw == null ? "" : String(discRaw)
    );
    if (Number.isNaN(discParsed)) {
      if (!quiet) {
        flash(
          "درصد تخفیف نامعتبر است. عدد ۰ تا ۱۰۰ با حداکثر دو رقم اعشار وارد کنید.",
          "error"
        );
      }
      return false;
    }
    const discount_percent = discParsed == null ? 0 : discParsed;
    try {
      const { res, json } = await safeJsonFetch(
        `${base}/admin/quote_requests/${quoteIdOf(selected)}/lines/${lineId}/price`,
        {
          method: "POST",
          headers: await quoteAdminHeaders(),
          body: JSON.stringify({ unit_price_irr: unit, discount_percent }),
        }
      );
      if (!res.ok) {
        if (!quiet) flash(json.error || "قیمت‌گذاری ناموفق", "error");
        return false;
      }
      if (!quiet) {
        flash("قیمت و درصد تخفیف ذخیره شد", "success");
        openQuote(quoteIdOf(selected));
        refreshList();
      }
      return true;
    } catch (_) {
      if (!quiet) flash("خطای شبکه — قیمت ذخیره نشد", "error");
      return false;
    }
  }

  async function saveShipping(opts) {
    if (!selected) return false;
    const quiet = opts && opts.quiet;
    const base = getQuoteApiBase();
    const parsed = parseIrrInput(
      shippingDraft == null || shippingDraft === "" ? "0" : String(shippingDraft)
    );
    if (parsed == null || Number.isNaN(parsed)) {
      if (!quiet) {
        flash(
          "هزینه ارسال نامعتبر است. مبلغ صحیح غیرمنفی به ریال وارد کنید.",
          "error"
        );
      }
      return false;
    }
    try {
      const { res, json } = await safeJsonFetch(
        `${base}/admin/quote_requests/${quoteIdOf(selected)}/shipping`,
        {
          method: "POST",
          headers: await quoteAdminHeaders(),
          body: JSON.stringify({ shipping_irr: parsed }),
        }
      );
      if (!res.ok) {
        if (!quiet) flash(json.error || "ذخیره هزینه ارسال ناموفق", "error");
        return false;
      }
      if (!quiet) {
        flash("هزینه ارسال ذخیره شد (ریال)", "success");
        openQuote(quoteIdOf(selected));
        refreshList();
      }
      return true;
    } catch (_) {
      if (!quiet) flash("خطای شبکه — هزینه ارسال ذخیره نشد", "error");
      return false;
    }
  }

  async function persistUnsavedDrafts() {
    const unsaved = [];
    for (const l of lines) {
      const id = String(l._id);
      const draftRaw = priceDraft[id];
      const draftNum =
        draftRaw === "" || draftRaw == null
          ? null
          : parseIrrInput(String(draftRaw));
      const discRaw = discountDraft[id];
      const discNum = parseDiscountPercentInput(
        discRaw == null ? "" : String(discRaw)
      );
      if (Number.isNaN(discNum)) {
        flash(
          `درصد تخفیف برای «${l.title || id}» نامعتبر است.`,
          "error"
        );
        return false;
      }
      const savedPct =
        l.discount_percent == null || l.discount_percent === ""
          ? 0
          : Number(l.discount_percent) || 0;
      const nextPct = discNum == null ? 0 : discNum;
      const priceChanged =
        draftNum != null &&
        !Number.isNaN(draftNum) &&
        (l.unit_price_irr == null || Number(l.unit_price_irr) !== draftNum);
      const pctChanged = Math.round(savedPct * 100) !== Math.round(nextPct * 100);
      if (priceChanged || (pctChanged && (draftNum != null || l.unit_price_irr != null))) {
        if (draftNum == null && l.unit_price_irr == null) continue;
        unsaved.push(l._id);
      }
    }
    for (const lineId of unsaved) {
      const ok = await savePrice(lineId, { quiet: true });
      if (!ok) {
        const line = lines.find((x) => String(x._id) === String(lineId));
        const title = line
          ? `${line.title}${line.size ? ` (سایز ${line.size})` : ""}`
          : String(lineId);
        flash(`ذخیره قیمت برای «${title}» ناموفق بود.`, "error");
        return false;
      }
    }
    const shipParsed = parseIrrInput(
      shippingDraft == null || shippingDraft === "" ? "0" : String(shippingDraft)
    );
    const savedShip = Number(selected.shipping_irr || 0);
    if (
      shipParsed != null &&
      !Number.isNaN(shipParsed) &&
      shipParsed !== savedShip
    ) {
      const okShip = await saveShipping({ quiet: true });
      if (!okShip) {
        flash("ذخیره هزینه ارسال ناموفق بود.", "error");
        return false;
      }
    }
    return true;
  }

  async function approve() {
    if (!selected || actionBusy) {
      if (!selected) flash("ابتدا یک درخواست را با «جزئیات» باز کنید.", "error");
      return;
    }
    if (
      selected.status === "QUOTE_APPROVED" ||
      selected.status === "QUOTE_SENT"
    ) {
      flash(
        selected.status === "QUOTE_SENT"
          ? "این پیش‌فاکتور قبلاً ارسال شده و قابل تأیید مجدد نیست."
          : "این پیش‌فاکتور قبلاً تأیید شده است.",
        "error"
      );
      return;
    }

    const missing = missingPriceLabels(lines, priceDraft);
    if (missing.length) {
      flash(
        `قبل از تأیید، این اقلام باید قیمت‌گذاری شوند: ${missing.join("، ")}`,
        "error"
      );
      return;
    }

    setActionBusy(true);
    flash("در حال ذخیره قیمت‌ها و تأیید پیش‌فاکتور…", "info");
    try {
      const saved = await persistUnsavedDrafts();
      if (!saved) {
        setActionBusy(false);
        return;
      }
      const base = getQuoteApiBase();
      const { res, json } = await safeJsonFetch(
        `${base}/admin/quote_requests/${quoteIdOf(selected)}/approve`,
        {
          method: "POST",
          headers: await quoteAdminHeaders(),
          body: JSON.stringify({ ttl_hours: 72 }),
        }
      );
      if (!res.ok) {
        const detail =
          Array.isArray(json.missing_lines) && json.missing_lines.length
            ? ` ${json.missing_lines.join("، ")}`
            : "";
        flash((json.error || "تأیید ناموفق") + detail, "error");
        setActionBusy(false);
        return;
      }
      flash(
        json.success_fa ||
          `پیش‌فاکتور تأیید شد — مبلغ کل ${Number(
            json.data?.total_irr || 0
          ).toLocaleString("fa-IR")} ریال`,
        "success"
      );
      openQuote(quoteIdOf(selected));
      refreshList();
    } catch (_) {
      flash("خطای شبکه — تأیید انجام نشد", "error");
    } finally {
      setActionBusy(false);
    }
  }

  async function sendToCustomer() {
    if (!selected || actionBusy) {
      if (!selected) flash("ابتدا یک درخواست را با «جزئیات» باز کنید.", "error");
      return;
    }
    if (selected.status === "QUOTE_SENT") {
      flash(
        "این پیش‌فاکتور قبلاً برای مشتری ارسال شده است. ارسال مجدد انجام نشد.",
        "error"
      );
      return;
    }
    if (selected.status !== "QUOTE_APPROVED") {
      flash(
        "ابتدا باید پیش‌فاکتور را با «تأیید پیش‌فاکتور» تأیید کنید؛ سپس ارسال ممکن است.",
        "error"
      );
      return;
    }

    setActionBusy(true);
    flash("در حال ارسال پیش‌فاکتور به مشتری…", "info");
    try {
      const base = getQuoteApiBase();
      const { res, json } = await safeJsonFetch(
        `${base}/admin/quote_requests/${quoteIdOf(selected)}/send`,
        { method: "POST", headers: await quoteAdminHeaders(), body: "{}" }
      );
      if (!res.ok) {
        flash(json.error || "ارسال ناموفق", "error");
        setActionBusy(false);
        return;
      }
      const link = rewriteCustomerLink(json.data?.customer_link || "");
      setCustomerLink(link);
      flash(
        json.success_fa ||
          "پیش‌فاکتور ارسال شد. لینک مشتری آماده است — در صورت نیاز کپی کنید.",
        "success"
      );
      openQuote(quoteIdOf(selected));
      refreshList();
    } catch (_) {
      flash("خطای شبکه — ارسال انجام نشد", "error");
    } finally {
      setActionBusy(false);
    }
  }

  async function fulfill(status) {
    if (!selected || actionBusy) return;
    const base = getQuoteApiBase();
    try {
      const { res, json } = await safeJsonFetch(
        `${base}/admin/quote_requests/${quoteIdOf(selected)}/fulfillment`,
        {
          method: "POST",
          headers: await quoteAdminHeaders(),
          body: JSON.stringify({ status }),
        }
      );
      if (!res.ok) {
        flash(json.error || "تغییر وضعیت نامعتبر", "error");
        return;
      }
      flash(`وضعیت → ${STATUS_FA[status] || status}`, "success");
      openQuote(quoteIdOf(selected));
      refreshList();
    } catch (_) {
      flash("خطای شبکه — تغییر وضعیت انجام نشد", "error");
    }
  }

  const summary = useMemo(() => {
    const s = {
      total: quotes.length,
      pending: 0,
      sent: 0,
      canceled: 0,
      expired: 0,
    };
    quotes.forEach((q) => {
      if (q.status === "CANCELED") s.canceled += 1;
      else if (q.status === "EXPIRED") s.expired += 1;
      else if (PENDING_STATUSES.has(q.status)) s.pending += 1;
      else if (SENT_STATUSES.has(q.status)) s.sent += 1;
    });
    return s;
  }, [quotes]);

  const filteredQuotes = useMemo(() => {
    const qText = String(search || "")
      .trim()
      .toLowerCase();
    const from = dayStart(dateFrom);
    const to = dayEnd(dateTo);

    return quotes.filter((q) => {
      if (statusFilter === "pending" && !PENDING_STATUSES.has(q.status)) {
        return false;
      }
      if (statusFilter === "sent" && !SENT_STATUSES.has(q.status)) {
        return false;
      }
      if (statusFilter === "canceled" && q.status !== "CANCELED") return false;
      if (statusFilter === "expired" && q.status !== "EXPIRED") return false;
      if (
        statusFilter !== "all" &&
        statusFilter !== "pending" &&
        statusFilter !== "sent" &&
        statusFilter !== "canceled" &&
        statusFilter !== "expired" &&
        q.status !== statusFilter
      ) {
        return false;
      }

      if (from || to) {
        const created = q.creation_date ? new Date(q.creation_date) : null;
        if (!created || Number.isNaN(created.getTime())) return false;
        if (from && created < from) return false;
        if (to && created > to) return false;
      }

      if (!qText) return true;
      const hay = [
        q.quote_ref,
        q.customer?.name,
        q.customer?.mobile,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return hay.includes(qText);
    });
  }, [quotes, search, statusFilter, dateFrom, dateTo]);

  function clearFilters() {
    setSearch("");
    setStatusFilter("all");
    setDateFrom("");
    setDateTo("");
  }

  function renderStatusBadge(status) {
    return (
      <span className={`${styles.badge} ${statusBadgeClass(status)}`}>
        {STATUS_FA[status] || status}
      </span>
    );
  }

  const detailPanel = (
    <div ref={detailRef} id="quote-detail-panel" className={styles.detailCard}>
      <div className={styles.detailHead}>
        <h2>جزئیات درخواست قیمت</h2>
        {(selected || detailError) && !detailLoading ? (
          <button type="button" className={styles.btnGhost} onClick={closeDetail}>
            بستن
          </button>
        ) : null}
      </div>

      {detailLoading ? (
        <p className={styles.loading}>در حال بارگذاری جزئیات…</p>
      ) : null}

      {detailError ? (
        <div className={`${styles.alert} ${styles.alertError}`}>{detailError}</div>
      ) : null}

      {!detailLoading && !detailError && !selected ? (
        <p style={{ margin: 0, color: "#64748b", fontSize: 15 }}>
          برای مشاهده مشتری، اقلام، سایز و تعداد، روی «جزئیات» در جدول زیر کلیک
          کنید. پنل همین‌جا (بالای جدول) باز می‌شود.
        </p>
      ) : null}

      {selected && !detailLoading ? (
        <>
          <header className={styles.qHeader}>
            <div>
              <div className={styles.qRef}>{selected.quote_ref}</div>
              <div className={styles.qDates}>
                <span>
                  <strong>ثبت:</strong>{" "}
                  {formatFaDateTime(
                    selected.creation_date || selected.createdAt
                  )}
                </span>
                <span>
                  <strong>انقضا:</strong>{" "}
                  {formatFaDateTime(selected.expires_at)}
                </span>
              </div>
            </div>
            {renderStatusBadge(selected.status)}
          </header>

          <section className={styles.detailSection} aria-labelledby="quote-cust">
            <h3 className={styles.detailSectionTitle} id="quote-cust">
              اطلاعات مشتری
            </h3>
            <div className={styles.infoGrid}>
              <div className={styles.infoField}>
                <span className={styles.infoLabel}>نام</span>
                <div className={styles.infoValue}>
                  {selected.customer?.name || "—"}
                </div>
              </div>
              <div className={styles.infoField}>
                <span className={styles.infoLabel}>موبایل</span>
                <div className={styles.infoValue} dir="ltr" style={{ textAlign: "right" }}>
                  {selected.customer?.mobile || "—"}
                </div>
              </div>
              <div className={styles.infoField}>
                <span className={styles.infoLabel}>وضعیت پرداخت</span>
                <div className={styles.infoValue}>
                  {PAYMENT_STATUS_FA[selected.payment_status] ||
                    selected.payment_status ||
                    PAYMENT_STATUS_FA.none}
                </div>
              </div>
              {selected.customer?.notes ? (
                <div className={styles.notesBlock}>
                  <span className={styles.infoLabel}>توضیحات مشتری</span>
                  <div className={styles.notesText}>
                    {selected.customer.notes}
                  </div>
                </div>
              ) : null}
            </div>
          </section>

          <section className={styles.detailSection} aria-labelledby="quote-items">
            <h3 className={styles.detailSectionTitle} id="quote-items">
              اقلام و قیمت‌گذاری
            </h3>
            {lines.length === 0 ? (
              <p style={{ color: "#c8102e" }}>ردیفی برای این درخواست یافت نشد.</p>
            ) : (
              <>
                <div className={styles.itemsTableWrap}>
                  <table className={styles.itemsTable}>
                    <thead>
                      <tr>
                        <th>محصول</th>
                        <th>سایز / مشخصات</th>
                        <th>تعداد</th>
                        <th>فی واحد (ریال)</th>
                        <th>درصد تخفیف</th>
                        <th>جمع ردیف</th>
                        <th>عملیات</th>
                      </tr>
                    </thead>
                    <tbody>
                      {lines.map((l) => {
                        const gross = lineGrossIrr(l, priceDraft);
                        const discAmt = lineDiscountAmountIrr(
                          l,
                          priceDraft,
                          discountDraft
                        );
                        const net = lineNetIrr(l, priceDraft, discountDraft);
                        const spec =
                          Array.isArray(l.property) && l.property.length
                            ? l.property
                                .map(
                                  (p) =>
                                    `${p.title || ""} ${p.description || ""}`.trim()
                                )
                                .filter(Boolean)
                                .join(" | ")
                            : "";
                        return (
                          <tr key={String(l._id)}>
                            <td>
                              {l.title || "—"}
                              {spec ? (
                                <span className={styles.specMuted}>{spec}</span>
                              ) : null}
                            </td>
                            <td>{l.size || "—"}</td>
                            <td>{l.qty}</td>
                            <td className={styles.priceCell}>
                              <div className={styles.priceFieldStack}>
                                <IrrPriceInput
                                  id={`irr-price-${l._id}`}
                                  value={priceDraft[String(l._id)] || ""}
                                  onValueChange={(digits) =>
                                    setPriceDraft((d) => ({
                                      ...d,
                                      [String(l._id)]: digits,
                                    }))
                                  }
                                />
                                {l.unit_price_irr != null ? (
                                  <span className={styles.fieldHint}>
                                    ذخیره‌شده:{" "}
                                    {Number(l.unit_price_irr).toLocaleString(
                                      "en-US"
                                    )}{" "}
                                    ریال
                                  </span>
                                ) : (
                                  <span
                                    className={styles.fieldHint}
                                    style={{ color: "#c8102e" }}
                                  >
                                    هنوز قیمت‌گذاری نشده
                                  </span>
                                )}
                              </div>
                            </td>
                            <td className={styles.discountCell}>
                              <div className={styles.discountPctWrap}>
                                <input
                                  id={`disc-pct-${l._id}`}
                                  className={styles.discountPctInput}
                                  inputMode="decimal"
                                  dir="ltr"
                                  placeholder="0"
                                  aria-label="درصد تخفیف"
                                  value={
                                    Object.prototype.hasOwnProperty.call(
                                      discountDraft,
                                      String(l._id)
                                    )
                                      ? discountDraft[String(l._id)]
                                      : ""
                                  }
                                  onChange={(e) => {
                                    const v = e.target.value;
                                    setDiscountDraft((d) => ({
                                      ...d,
                                      [String(l._id)]: v,
                                    }));
                                  }}
                                />
                                <span
                                  className={styles.discountPctSuffix}
                                  aria-hidden="true"
                                >
                                  ٪
                                </span>
                              </div>
                            </td>
                            <td className={styles.lineTotal}>
                              {net == null ? (
                                "—"
                              ) : (
                                <>
                                  <div>
                                    {Number(net).toLocaleString("fa-IR")} ریال
                                  </div>
                                  {discAmt > 0 ? (
                                    <span className={styles.fieldHint}>
                                      قبل:{" "}
                                      {Number(gross).toLocaleString("fa-IR")} ·
                                      تخفیف:{" "}
                                      {Number(discAmt).toLocaleString("fa-IR")}
                                    </span>
                                  ) : null}
                                </>
                              )}
                            </td>
                            <td className={styles.actionCell}>
                              <button
                                type="button"
                                className={styles.btnWarn}
                                onClick={() => savePrice(l._id)}
                              >
                                ذخیره قیمت
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                <div className={styles.itemCards}>
                  {lines.map((l) => {
                    const gross = lineGrossIrr(l, priceDraft);
                    const discAmt = lineDiscountAmountIrr(
                      l,
                      priceDraft,
                      discountDraft
                    );
                    const net = lineNetIrr(l, priceDraft, discountDraft);
                    const spec =
                      Array.isArray(l.property) && l.property.length
                        ? l.property
                            .map(
                              (p) =>
                                `${p.title || ""} ${p.description || ""}`.trim()
                            )
                            .filter(Boolean)
                            .join(" | ")
                        : "";
                    return (
                      <div key={`card-${l._id}`} className={styles.itemCard}>
                        <div className={styles.itemCardTitle}>
                          {l.title || "—"}
                        </div>
                        <div className={styles.itemCardMeta}>
                          سایز: {l.size || "—"} · تعداد: {l.qty}
                          {spec ? (
                            <>
                              <br />
                              {spec}
                            </>
                          ) : null}
                        </div>
                        <div className={styles.itemCardActions}>
                          <label
                            htmlFor={`irr-price-m-${l._id}`}
                            className={styles.itemCardField}
                          >
                            <span className={styles.itemCardFieldLabel}>
                              فی واحد (ریال)
                            </span>
                            <IrrPriceInput
                              id={`irr-price-m-${l._id}`}
                              value={priceDraft[String(l._id)] || ""}
                              onValueChange={(digits) =>
                                setPriceDraft((d) => ({
                                  ...d,
                                  [String(l._id)]: digits,
                                }))
                              }
                            />
                          </label>
                          <label
                            htmlFor={`disc-pct-m-${l._id}`}
                            className={styles.itemCardField}
                          >
                            <span className={styles.itemCardFieldLabel}>
                              درصد تخفیف
                            </span>
                            <div className={styles.discountPctWrap}>
                              <input
                                id={`disc-pct-m-${l._id}`}
                                className={styles.discountPctInput}
                                inputMode="decimal"
                                dir="ltr"
                                placeholder="0"
                                value={
                                  Object.prototype.hasOwnProperty.call(
                                    discountDraft,
                                    String(l._id)
                                  )
                                    ? discountDraft[String(l._id)]
                                    : ""
                                }
                                onChange={(e) => {
                                  const v = e.target.value;
                                  setDiscountDraft((d) => ({
                                    ...d,
                                    [String(l._id)]: v,
                                  }));
                                }}
                              />
                              <span
                                className={styles.discountPctSuffix}
                                aria-hidden="true"
                              >
                                ٪
                              </span>
                            </div>
                          </label>
                          <button
                            type="button"
                            className={styles.btnWarn}
                            onClick={() => savePrice(l._id)}
                          >
                            ذخیره قیمت
                          </button>
                          <span className={styles.lineTotal}>
                            {net == null
                              ? "جمع پس از تخفیف: —"
                              : `جمع پس از تخفیف: ${Number(net).toLocaleString(
                                  "fa-IR"
                                )} ریال`}
                            {discAmt > 0 ? (
                              <>
                                <br />
                                <span className={styles.fieldHint}>
                                  قبل: {Number(gross).toLocaleString("fa-IR")} ·
                                  تخفیف:{" "}
                                  {Number(discAmt).toLocaleString("fa-IR")}
                                </span>
                              </>
                            ) : null}
                          </span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </>
            )}
          </section>

          <section className={styles.detailSection} aria-labelledby="quote-sum">
            <h3 className={styles.detailSectionTitle} id="quote-sum">
              جمع‌بندی
            </h3>
            <div className={styles.summaryBox}>
              <div className={styles.summaryRow}>
                <span className={styles.summaryLabel}>
                  جمع اقلام قبل از تخفیف (ریال)
                </span>
                <strong className={styles.summaryValue}>
                  {Number(selected.subtotal_irr || 0).toLocaleString("fa-IR")}
                </strong>
              </div>
              <div className={styles.summaryRow}>
                <span className={styles.summaryLabel}>
                  جمع تخفیف اقلام (ریال)
                </span>
                <strong className={styles.summaryValue}>
                  {Number(selected.discount_irr || 0).toLocaleString("fa-IR")}
                </strong>
              </div>
              <div className={styles.summaryRow}>
                <span className={styles.summaryLabel}>
                  جمع پس از تخفیف (ریال)
                </span>
                <strong className={styles.summaryValue}>
                  {Math.max(
                    0,
                    Number(selected.subtotal_irr || 0) -
                      Number(selected.discount_irr || 0)
                  ).toLocaleString("fa-IR")}
                </strong>
              </div>
              <div
                className={`${styles.summaryRow} ${styles.summaryRowShipping}`}
              >
                <span className={styles.summaryLabel}>هزینه ارسال (ریال)</span>
                <div className={styles.summaryValueStack}>
                  <div className={styles.shippingEdit}>
                    <IrrPriceInput
                      id="quote-shipping-irr"
                      value={shippingDraft}
                      onValueChange={(digits) => setShippingDraft(digits)}
                    />
                    <button
                      type="button"
                      className={styles.btnGhost}
                      onClick={() => saveShipping()}
                    >
                      ذخیره ارسال
                    </button>
                  </div>
                  <span className={styles.fieldHint}>
                    ذخیره‌شده ارسال:{" "}
                    {Number(selected.shipping_irr || 0).toLocaleString("fa-IR")}{" "}
                    ریال
                  </span>
                </div>
              </div>
              <div className={styles.summaryRow}>
                <span className={styles.summaryLabel}>
                  مالیات
                  {selected.vat_percent != null
                    ? ` (${selected.vat_percent}٪)`
                    : ""}{" "}
                  (ریال)
                </span>
                <strong className={styles.summaryValue}>
                  {Number(selected.tax_irr || 0).toLocaleString("fa-IR")}
                </strong>
              </div>
              <div className={styles.summaryRow}>
                <span className={styles.summaryLabel}>نمایش تومان (نهایی)</span>
                <strong className={styles.summaryValue}>
                  {toman(selected.total_irr)} تومان
                </strong>
              </div>
              <div className={`${styles.summaryRow} ${styles.summaryRowTotal}`}>
                <span className={styles.summaryLabel}>مبلغ کل</span>
                <strong className={styles.summaryValue}>
                  {Number(selected.total_irr || 0).toLocaleString("fa-IR")} ریال ·{" "}
                  {toman(selected.total_irr)} تومان
                </strong>
              </div>
            </div>
          </section>

          <section className={styles.actionsBlock} aria-labelledby="quote-act">
            <h3 className={styles.detailSectionTitle} id="quote-act">
              اقدامات
            </h3>
            <div className={styles.actionGroups}>
              <div>
                <div className={styles.actionGroupLabel}>پیش‌فاکتور / کاربرگ</div>
                <div className={styles.actionRow}>
                  <button
                    type="button"
                    className={styles.btnGhost}
                    disabled={actionBusy}
                    onClick={() =>
                      downloadProformaPdf(
                        `${getQuoteApiBase()}/admin/quote_requests/${encodeURIComponent(
                          quoteIdOf(selected)
                        )}/proforma/worksheet.pdf`,
                        `worksheet-${selected.quote_ref}.pdf`
                      ).catch(() => flash("دانلود کاربرگ ناموفق بود", "error"))
                    }
                  >
                    کاربرگ خالی (PDF)
                  </button>
                  <button
                    type="button"
                    className={styles.btnGhost}
                    disabled={actionBusy}
                    onClick={() =>
                      printProformaHtml(
                        `${getQuoteApiBase()}/admin/quote_requests/${encodeURIComponent(
                          quoteIdOf(selected)
                        )}/proforma/worksheet.html`
                      ).catch(() => flash("چاپ کاربرگ ناموفق بود", "error"))
                    }
                  >
                    کاربرگ (چاپ)
                  </button>
                  {CUSTOMER_PROFORMA_STATUSES.includes(selected.status) ? (
                    <button
                      type="button"
                      className={styles.btnGhost}
                      disabled={actionBusy}
                      onClick={() =>
                        downloadProformaPdf(
                          `${getQuoteApiBase()}/admin/quote_requests/${encodeURIComponent(
                            quoteIdOf(selected)
                          )}/proforma/customer.pdf`,
                          `proforma-${selected.quote_ref}.pdf`
                        ).catch(() =>
                          flash("دانلود پیش‌فاکتور مشتری ناموفق بود", "error")
                        )
                      }
                    >
                      پیش‌فاکتور مشتری (PDF)
                    </button>
                  ) : null}
                </div>
              </div>
              <div>
                <div className={styles.actionGroupLabel}>گردش پیش‌فاکتور</div>
                <div className={styles.actionRow}>
                  <button
                    type="button"
                    className={styles.btnSuccess}
                    onClick={approve}
                    disabled={
                      actionBusy ||
                      selected.status === "QUOTE_APPROVED" ||
                      selected.status === "QUOTE_SENT"
                    }
                    title={
                      selected.status === "QUOTE_APPROVED" ||
                      selected.status === "QUOTE_SENT"
                        ? "قبلاً تأیید شده"
                        : "همه اقلام باید قیمت ذخیره‌شده داشته باشند"
                    }
                  >
                    {actionBusy ? "…" : "تأیید پیش‌فاکتور"}
                  </button>
                  <button
                    type="button"
                    className={styles.btnNavy}
                    onClick={sendToCustomer}
                    disabled={
                      actionBusy ||
                      selected.status !== "QUOTE_APPROVED" ||
                      selected.status === "QUOTE_SENT"
                    }
                    title={
                      selected.status === "QUOTE_SENT"
                        ? "قبلاً ارسال شده"
                        : selected.status !== "QUOTE_APPROVED"
                          ? "ابتدا تأیید کنید"
                          : "ارسال لینک مشتری و پیامک طبق پیکربندی سرور"
                    }
                  >
                    {actionBusy ? "…" : "ارسال به مشتری + پیامک"}
                  </button>
                </div>
              </div>
              <div>
                <div className={styles.actionGroupLabel}>
                  وضعیت اجرا (پس از ارسال)
                </div>
                <div className={styles.actionRow}>
                  <button
                    type="button"
                    className={styles.btnGhost}
                    onClick={() => fulfill("PREPARING")}
                    disabled={actionBusy}
                  >
                    آماده‌سازی
                  </button>
                  <button
                    type="button"
                    className={styles.btnGhost}
                    onClick={() => fulfill("SHIPPED")}
                    disabled={actionBusy}
                  >
                    ارسال شده
                  </button>
                  <button
                    type="button"
                    className={styles.btnGhost}
                    onClick={() => fulfill("COMPLETED")}
                    disabled={actionBusy}
                  >
                    تکمیل
                  </button>
                </div>
              </div>
            </div>
            {selected.status === "QUOTE_APPROVED" ? (
              <p className={styles.hintOk}>
                تأیید شده — اکنون می‌توانید «ارسال به مشتری + پیامک» را بزنید.
              </p>
            ) : null}
            {selected.status === "QUOTE_SENT" ? (
              <p className={styles.hintOk}>
                لینک مشتری ساخته شده است. در صورت پذیرش ارائه‌دهنده، پیامک نیز
                ارسال شده است — لینک را از بالا کپی کنید.
              </p>
            ) : null}
          </section>
        </>
      ) : null}
    </div>
  );

  return (
    <div className={styles.root}>
      <div className={styles.pageHead}>
        <div className={styles.titleBlock}>
          <h1>
            درخواست‌های قیمت
            {unread > 0 ? (
              <span className={styles.unreadPill}>{unread} خوانده‌نشده</span>
            ) : null}
          </h1>
          <p className={styles.subtitle}>
            پنل ایزوله · API <code>{apiBase}</code> · دیتابیس{" "}
            <code>pipeup_quote_dev</code>
          </p>
        </div>
        <div className={styles.toolbar}>
          <button
            type="button"
            className={styles.btnGhost}
            onClick={() => {
              setLoading(true);
              refreshList();
              refreshUnread();
            }}
          >
            بروزرسانی
          </button>
          <button
            type="button"
            className={soundOn ? styles.btnSuccess : styles.btnGhost}
            onClick={enableSound}
          >
            {soundOn ? "صدای اعلان فعال" : "فعال‌سازی صدا"}
          </button>
          <button
            type="button"
            className={styles.btnGhost}
            onClick={testSound}
            title="پخش همان صدای اعلان سفارش"
          >
            تست صدا
          </button>
          <button
            type="button"
            className={styles.btnGhost}
            onClick={async () => {
              const base = getQuoteApiBase();
              try {
                const { json } = await safeJsonFetch(
                  `${base}/admin/quote_notifications?unread=1`,
                  { headers: await quoteAdminHeaders() }
                );
                const ids = (json.data?.docs || []).map((d) => d._id);
                await markNotificationsRead(ids);
              } catch (_) {
                flash("خواندن اعلان‌ها ناموفق", "error");
              }
            }}
          >
            خواندن اعلان‌ها
          </button>
        </div>
      </div>

      {connError ? (
        <div className={`${styles.alert} ${styles.alertError}`}>
          <strong>اتصال API برقرار نشد.</strong>
          <div>{connError}</div>
          <button
            type="button"
            className={styles.btnDanger}
            style={{ marginTop: 10 }}
            onClick={() => {
              setLoading(true);
              refreshList();
              refreshUnread();
            }}
          >
            تلاش مجدد
          </button>
        </div>
      ) : null}

      <div className={styles.stats}>
        {[
          {
            key: "all",
            label: "کل درخواست‌ها",
            value: summary.total,
            cls: styles.statTotal,
          },
          {
            key: "pending",
            label: "در انتظار / جاری",
            value: summary.pending,
            cls: styles.statPending,
          },
          {
            key: "sent",
            label: "ارسال‌شده",
            value: summary.sent,
            cls: styles.statSent,
          },
          {
            key: "canceled",
            label: "لغوشده",
            value: summary.canceled,
            cls: styles.statCanceled,
          },
          {
            key: "expired",
            label: "منقضی‌شده",
            value: summary.expired,
            cls: styles.statExpired,
          },
        ].map((card) => (
          <div
            key={card.key}
            className={`${styles.statCard} ${card.cls} ${
              statusFilter === card.key ? styles.statActive : ""
            }`}
          >
            <button
              type="button"
              onClick={() => setStatusFilter(card.key)}
              aria-pressed={statusFilter === card.key}
            >
              <div className={styles.statLabel}>{card.label}</div>
              <div className={styles.statValue}>
                {Number(card.value).toLocaleString("fa-IR")}
              </div>
            </button>
          </div>
        ))}
      </div>

      <div className={styles.filters}>
        <div className={styles.field}>
          <label htmlFor="quote-search">جستجو</label>
          <input
            id="quote-search"
            type="search"
            placeholder="شماره پیگیری، نام مشتری یا موبایل…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <div className={styles.field}>
          <label htmlFor="quote-status">وضعیت</label>
          <select
            id="quote-status"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="all">همه</option>
            <option value="pending">در انتظار / جاری</option>
            <option value="sent">ارسال‌شده و پس از آن</option>
            <option value="canceled">لغوشده</option>
            <option value="expired">منقضی‌شده</option>
            <option value="QUOTE_REQUESTED">درخواست ثبت‌شده</option>
            <option value="PRICING_IN_PROGRESS">در حال قیمت‌گذاری</option>
            <option value="QUOTE_APPROVED">تأییدشده</option>
            <option value="QUOTE_SENT">ارسال‌شده</option>
          </select>
        </div>
        <div className={styles.field}>
          <label htmlFor="quote-from">از تاریخ</label>
          <input
            id="quote-from"
            type="date"
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
          />
        </div>
        <div className={styles.field}>
          <label htmlFor="quote-to">تا تاریخ</label>
          <input
            id="quote-to"
            type="date"
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
          />
        </div>
        <div className={styles.field}>
          <label>&nbsp;</label>
          <button type="button" className={styles.btnGhost} onClick={clearFilters}>
            پاک کردن فیلتر
          </button>
        </div>
      </div>

      {msg ? (
        <div
          role="status"
          className={`${styles.alert} ${
            msgKind === "error"
              ? styles.alertError
              : msgKind === "success"
                ? styles.alertOk
                : styles.alertInfo
          }`}
        >
          {msg}
        </div>
      ) : null}

      {customerLink ? (
        <p className={styles.linkLine}>
          لینک مشتری:{" "}
          <a href={customerLink} target="_blank" rel="noreferrer">
            {customerLink}
          </a>
        </p>
      ) : null}

      {detailPanel}

      <div className={styles.tableCard}>
        {loading && !connError ? (
          <div className={styles.loading}>در حال بارگذاری لیست…</div>
        ) : filteredQuotes.length === 0 && !connError ? (
          <div className={styles.empty}>
            {quotes.length === 0
              ? "درخواستی ثبت نشده است."
              : "موردی با این فیلتر/جستجو یافت نشد."}
          </div>
        ) : (
          <>
            <div className={styles.tableWrap}>
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th>شماره پیگیری</th>
                    <th>مشتری</th>
                    <th>وضعیت</th>
                    <th>جمع (تومان)</th>
                    <th>تاریخ</th>
                    <th>عملیات</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredQuotes.map((q) => {
                    const id = quoteIdOf(q);
                    const isOpen = selected && quoteIdOf(selected) === id;
                    return (
                      <tr
                        key={id || q.quote_ref}
                        className={`${styles.rowHover} ${
                          isOpen ? styles.rowOpen : ""
                        }`}
                      >
                        <td className={styles.refCell}>{q.quote_ref}</td>
                        <td className={styles.customerCell}>
                          {q.customer?.name || "—"}
                          <small>{q.customer?.mobile}</small>
                        </td>
                        <td>{renderStatusBadge(q.status)}</td>
                        <td>{toman(q.total_irr)}</td>
                        <td>
                          {q.creation_date
                            ? new Date(q.creation_date).toLocaleString("fa-IR")
                            : "—"}
                        </td>
                        <td>
                          <button
                            type="button"
                            className={
                              isOpen ? styles.btnDetailOpen : styles.btnDetail
                            }
                            onClick={(e) => openQuote(id, e)}
                          >
                            {isOpen ? "باز است" : "جزئیات"}
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <div className={styles.mobileCards}>
              {filteredQuotes.map((q) => {
                const id = quoteIdOf(q);
                const isOpen = selected && quoteIdOf(selected) === id;
                return (
                  <div
                    key={`m-${id || q.quote_ref}`}
                    className={`${styles.mobileCard} ${
                      isOpen ? styles.mobileCardOpen : ""
                    }`}
                  >
                    <div className={styles.refCell}>{q.quote_ref}</div>
                    <div className={styles.customerCell}>
                      {q.customer?.name || "—"}
                      <small>{q.customer?.mobile}</small>
                    </div>
                    <div className={styles.mobileRow}>
                      {renderStatusBadge(q.status)}
                      <span>{toman(q.total_irr)} تومان</span>
                    </div>
                    <div className={styles.mobileRow}>
                      <span>
                        {q.creation_date
                          ? new Date(q.creation_date).toLocaleString("fa-IR")
                          : "—"}
                      </span>
                      <button
                        type="button"
                        className={
                          isOpen ? styles.btnDetailOpen : styles.btnDetail
                        }
                        onClick={(e) => openQuote(id, e)}
                      >
                        {isOpen ? "باز است" : "جزئیات"}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export { GetQuotes };
